import { ElementRef, Injectable } from '@angular/core';
import {
    LoggedInUser,
    PersonsOrganisationDto,
    UserService,
    UserStatus,
    hasAnyPermission
} from '@flyfreely-portal-ui/flyfreely';
import { TableSetupUserPreferences } from '@flyfreely-portal-ui/flyfreely-table';
import {
    WidgetManager,
    WidgetRegistration,
    WidgetStatus
} from '@flyfreely-portal-ui/widget';
import {
    CurrentOrganisation,
    SelectedOrganisation,
    WorkspaceStateService
} from '@flyfreely-portal-ui/workspace';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

export interface WidgetRegistrationWithSection extends WidgetRegistration {
    section:
        | 'onboarding'
        | 'general'
        | 'profile'
        | 'operations'
        | 'resources'
        | 'organisation';
    componentInputs?: { [key: string]: any };
    /**
     * Widget is shown only when the current organisation grants the logged-in
     * user at least one of these permissions. Omit to show regardless of permissions.
     */
    requiredPermissions?: PersonsOrganisationDto.Permissions[];
}
export interface OutstandingTasksViewSectionsSelection {
    onboardingTasks: boolean;
    generalTasks: boolean;
    profile: boolean;
    operations: boolean;
    resources: boolean;
    organisation: boolean;
}
export interface OutstandingTasksViewTableSetupUserPreferences
    extends TableSetupUserPreferences {
    showBody?: boolean;
}
@Injectable()
export class RegisteredTaskWidgets {
    constructor(
        public registeredTaskWidgets: WidgetRegistrationWithSection[]
    ) {}
}

@Injectable()
export class OutstandingTasksViewService extends WidgetManager {
    private scrollToSectionSubject = new Subject<string>();
    scrollToSection$ = this.scrollToSectionSubject.asObservable();

    private availableTestWidgetsSubject = new ReplaySubject<WidgetStatus>();
    availableTestWidgets$ = this.availableTestWidgetsSubject.asObservable();

    private selectedSectionsSource =
        new BehaviorSubject<OutstandingTasksViewSectionsSelection>({
            onboardingTasks: true,
            generalTasks: true,
            profile: true,
            operations: true,
            resources: true,
            organisation: true
        });
    selectedSections$ = this.selectedSectionsSource.asObservable();

    private organisation: PersonsOrganisationDto;
    private personalOrganisation: PersonsOrganisationDto;

    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        public registeredTaskWidgets: RegisteredTaskWidgets,
        private workspaceStateService: WorkspaceStateService,
        userService: UserService
    ) {
        super();
        userService.userChange$
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                filter(c => c.type === UserStatus.LOGGED_IN)
            )
            .subscribe((change: LoggedInUser) => {
                this.personalOrganisation =
                    userService.getPersonalOrganisation();
                this.refreshAvailableWidgets();
            });
        this.workspaceStateService.currentOrganisation$
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                filter(
                    (o: SelectedOrganisation) =>
                        o.type === 'organisation_loaded'
                )
            )
            .subscribe((organisation: CurrentOrganisation) => {
                this.organisation = organisation.organisation;
                this.refreshAvailableWidgets();
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.scrollToSectionSubject.complete();
        this.availableTestWidgetsSubject.complete();
        this.selectedSectionsSource.complete();
    }

    public scrollTo(sectionName: string) {
        this.scrollToSectionSubject.next(sectionName);
    }

    private refreshAvailableWidgets() {
        if (this.personalOrganisation == null || this.organisation == null) {
            return;
        }
        const updatedAvailableWidgets: WidgetStatus =
            this.registeredTaskWidgets.registeredTaskWidgets.reduce(
                (acc, reg) => {
                    const featureAvailable =
                        reg.isAvailableForUser != null
                            ? reg.isAvailableForUser(this.personalOrganisation)
                            : reg.isAvailable(this.organisation);

                    const permissionAvailable =
                        reg.requiredPermissions == null ||
                        reg.requiredPermissions.length === 0 ||
                        hasAnyPermission(
                            this.organisation,
                            reg.requiredPermissions
                        );

                    return {
                        ...acc,
                        [reg.widgetIdentifier]: {
                            available: featureAvailable && permissionAvailable,
                            // For now all widgets are always visible in this list.
                            selected: true
                        }
                    };
                },
                {} as WidgetStatus
            );
        this.availableTestWidgetsSubject.next(updatedAvailableWidgets);
    }

    splitWidgetsBySection(availableWidgets: WidgetStatus) {
        const onboardingTasksWidgets =
            this.registeredTaskWidgets.registeredTaskWidgets.filter(
                w =>
                    w.section === 'onboarding' &&
                    w.widgetIdentifier === 'onboardingTasksWidget' &&
                    availableWidgets[w.widgetIdentifier].available
            );
        const generalTasksWidgets =
            this.registeredTaskWidgets.registeredTaskWidgets.filter(
                w =>
                    w.section === 'general' &&
                    availableWidgets[w.widgetIdentifier].available
            );
        const personalProfileWidgets =
            this.registeredTaskWidgets.registeredTaskWidgets.filter(
                w =>
                    w.section === 'profile' &&
                    availableWidgets[w.widgetIdentifier].available
            );
        const operationsWidgets =
            this.registeredTaskWidgets.registeredTaskWidgets.filter(
                w =>
                    w.section === 'operations' &&
                    availableWidgets[w.widgetIdentifier].available
            );
        const resourcesWidgets =
            this.registeredTaskWidgets.registeredTaskWidgets.filter(
                w =>
                    w.section === 'resources' &&
                    availableWidgets[w.widgetIdentifier].available
            );
        const organisationWidgets =
            this.registeredTaskWidgets.registeredTaskWidgets.filter(
                w =>
                    w.section === 'organisation' &&
                    availableWidgets[w.widgetIdentifier].available
            );

        return [
            onboardingTasksWidgets,
            generalTasksWidgets,
            personalProfileWidgets,
            operationsWidgets,
            resourcesWidgets,
            organisationWidgets
        ];
    }

    updateSelectedSections(sections: OutstandingTasksViewSectionsSelection) {
        this.selectedSectionsSource.next(sections);
    }

    registerWidget(widgetId: string, element: ElementRef<any>): void {
        // do nothing
    }
    unregisterWidget(widgetId: string): void {
        // do nothing
    }
}
